/**
 * Travel companions as expandable cards, each saved on its own. A card edits a draft of its companion;
 * Save validates and stores that one companion (`onSave`), Cancel reverts the draft (or drops a card
 * that was never saved), and Delete removes a saved companion straight away after a confirmation.
 *
 * A saved companion's preferences are rendered by the parent (`renderPreferences`) — on the Family page,
 * a list that saves each change immediately. A new companion's preferences are typed into the draft and
 * created with them on their first Save.
 */
import type { AdditionalTraveller, Relationship } from '@elara/domain/models/schemas/TravelerProfileSchema';

import { useState } from 'react';

import { getCountriesList } from '@elara/client-core/lib/country-utils';
import {
  emptyTraveller,
  isChildRelationship,
  isTravellerValid,
  RELATIONSHIP_OPTIONS,
} from '@elara/client-core/lib/traveller-utils';
import { cn } from '@elara/client-core/lib/utils';

import { DateSelectField } from './DateSelectField.js';
import { Button, Card, FloatingInput, FloatingSelect, SelectItem } from './index.js';
import { MaterialIcon } from './MaterialIcon.js';
import { PreferenceList } from './PreferenceList.js';

const COUNTRIES = getCountriesList();

interface TravellerListProps {
  initial: AdditionalTraveller[];
  /** Stores one companion's details and resolves to their id (new or existing). */
  onSave: (traveller: AdditionalTraveller) => Promise<string>;
  /** Removes one saved companion. */
  onRemove: (traveller: AdditionalTraveller & { id: string }) => Promise<void>;
  /** If true, shows passport and nationality fields. Defaults to true. */
  showPassportFields?: boolean;
  /** Renders a saved companion's preferences. Omit to leave preferences off the cards. */
  renderPreferences?: (traveller: AdditionalTraveller & { id: string }) => React.ReactNode;
  /** Lets the parent start a new card, e.g. from a button in the page header. */
  controlRef?: React.RefObject<TravellerListControl | null>;
}

export interface TravellerListControl {
  /** Adds a blank, expanded card for a new companion. */
  addTraveller(): void;
}

/** One card: what's stored (absent until first saved) and the draft being edited. */
interface Item {
  key: string;
  saved?: AdditionalTraveller & { id: string };
  draft: AdditionalTraveller;
}

let nextKey = 0;
const newKey = () => `traveller-${++nextKey}`;

const displayName = (traveller: AdditionalTraveller) =>
  traveller.firstName && traveller.lastName ? `${traveller.firstName} ${traveller.lastName}` : 'New traveller';

export function TravellerList({
  initial,
  onSave,
  onRemove,
  showPassportFields = true,
  renderPreferences,
  controlRef,
}: TravellerListProps) {
  const [items, setItems] = useState<Item[]>(() =>
    initial.map((traveller) => ({
      key: traveller.id ?? newKey(),
      saved: traveller.id ? (traveller as AdditionalTraveller & { id: string }) : undefined,
      draft: traveller,
    })),
  );
  const [expandedKey, setExpandedKey] = useState<string | undefined>();
  const [validatingKey, setValidatingKey] = useState<string | undefined>();
  const [busyKey, setBusyKey] = useState<string | undefined>();
  const [error, setError] = useState<{ key: string; message: string } | undefined>();

  const addTraveller = () => {
    const key = newKey();
    setItems((prev) => [...prev, { key, draft: emptyTraveller() }]);
    setExpandedKey(key);
  };

  if (controlRef) {
    (controlRef as React.MutableRefObject<TravellerListControl | null>).current = { addTraveller };
  }

  const updateDraft = (key: string, updates: Partial<AdditionalTraveller>) => {
    setItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, draft: { ...item.draft, ...updates } as AdditionalTraveller } : item,
      ),
    );
  };

  const dropItem = (key: string) => {
    setItems((prev) => prev.filter((item) => item.key !== key));
    if (expandedKey === key) setExpandedKey(undefined);
  };

  const cancel = (item: Item) => {
    if (!item.saved) return dropItem(item.key);
    setItems((prev) => prev.map((other) => (other.key === item.key ? { ...other, draft: item.saved! } : other)));
    setValidatingKey(undefined);
    setExpandedKey(undefined);
  };

  const save = async (item: Item) => {
    if (!isTravellerValid(item.draft)) {
      setValidatingKey(item.key);
      return;
    }
    setBusyKey(item.key);
    setError(undefined);
    try {
      const id = await onSave({ ...item.draft, id: item.saved?.id });
      const saved = { ...item.draft, id };
      setItems((prev) => prev.map((other) => (other.key === item.key ? { ...other, saved, draft: saved } : other)));
      setValidatingKey(undefined);
      setExpandedKey(undefined);
    } catch (err) {
      console.error('[TravellerList] Failed to save companion', err);
      setError({ key: item.key, message: 'We couldn’t save this traveller. Please try again.' });
    } finally {
      setBusyKey(undefined);
    }
  };

  const remove = async (item: Item) => {
    if (!item.saved) return dropItem(item.key);
    if (!confirm(`Remove ${displayName(item.saved)} from your travellers?`)) return;
    setBusyKey(item.key);
    setError(undefined);
    try {
      await onRemove(item.saved);
      dropItem(item.key);
    } catch (err) {
      console.error('[TravellerList] Failed to remove companion', err);
      setError({ key: item.key, message: 'We couldn’t remove this traveller. Please try again.' });
    } finally {
      setBusyKey(undefined);
    }
  };

  return (
    <div className="space-y-4">
      {items.length === 0 && <p className="text-sm text-muted-foreground">No travellers yet.</p>}

      {items.map((item) => {
        const { key, draft, saved } = item;
        const isExpanded = expandedKey === key;
        const showValidation = validatingKey === key;
        const isDirty = saved !== undefined && JSON.stringify(draft) !== JSON.stringify(saved);
        const shown = isExpanded ? draft : (saved ?? draft);
        return (
          <Card
            key={key}
            className={cn(
              'border border-transparent overflow-hidden',
              isExpanded ? 'shadow-sm' : 'border-border hover:border-input',
            )}
          >
            <div
              className="flex items-center justify-between pl-6 pr-4 py-4 cursor-pointer transition-colors"
              onClick={() => setExpandedKey(isExpanded ? undefined : key)}
            >
              <div className="flex items-center gap-3">
                <MaterialIcon icon={isExpanded ? 'expand_less' : 'expand_more'} className="text-muted-foreground" />
                <div>
                  <span className="font-medium">{displayName(shown)}</span>
                  {shown.relationship && (
                    <span className="text-sm text-muted-foreground ml-2">
                      ({RELATIONSHIP_OPTIONS.find((r) => r.value === shown.relationship)?.label})
                    </span>
                  )}
                  {!isExpanded && (isDirty || !saved) && (
                    <span className="text-sm text-muted-foreground ml-2 italic">Unsaved changes</span>
                  )}
                </div>
              </div>
              <Button
                variant="icon"
                size="icon-sm"
                aria-label={`Remove ${displayName(shown)}`}
                isBusy={busyKey === key && !isExpanded}
                onClick={(e) => {
                  e.stopPropagation();
                  void remove(item);
                }}
                className="h-8 w-8 hover:text-destructive"
              >
                <MaterialIcon icon="delete" />
              </Button>
            </div>

            {isExpanded && (
              <div className="p-6 border-t space-y-6">
                <div className="space-y-3">
                  <div className="grid md:grid-cols-2 gap-6 md:gap-3">
                    <FloatingInput
                      label="First name"
                      required
                      value={draft.firstName}
                      onChange={(e) => updateDraft(key, { firstName: e.target.value })}
                      error={showValidation && !draft.firstName ? 'Required' : undefined}
                    />
                    <FloatingInput
                      label="Last name"
                      required
                      value={draft.lastName}
                      onChange={(e) => updateDraft(key, { lastName: e.target.value })}
                      error={showValidation && !draft.lastName ? 'Required' : undefined}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 md:gap-3">
                    <FloatingSelect
                      label="Relationship"
                      required
                      value={draft.relationship ?? ''}
                      onValueChange={(value) => updateDraft(key, { relationship: value as Relationship })}
                      error={showValidation && !draft.relationship ? 'Required' : undefined}
                    >
                      {RELATIONSHIP_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </FloatingSelect>

                    <DateSelectField
                      label="Date of birth"
                      value={draft.dateOfBirth ?? ''}
                      onChange={(value) => updateDraft(key, { dateOfBirth: value })}
                    />
                  </div>

                  {isChildRelationship(draft.relationship) && (
                    <div className="grid md:grid-cols-2 gap-6 md:gap-3">
                      <FloatingInput
                        label="Age (years)"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={120}
                        value={draft.age ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          updateDraft(key, { age: raw === '' ? undefined : Number(raw) });
                        }}
                      />
                    </div>
                  )}
                </div>

                {showPassportFields && (
                  <>
                    <hr />

                    <div className="space-y-3">
                      <div className="grid md:grid-cols-2 gap-6 md:gap-3">
                        <FloatingInput
                          label="Passport number"
                          value={draft.passportInfo?.number || ''}
                          onChange={(e) =>
                            updateDraft(key, { passportInfo: { ...draft.passportInfo, number: e.target.value } })
                          }
                        />
                        <FloatingSelect
                          label="Nationality"
                          value={draft.passportInfo?.nationality || ''}
                          onValueChange={(value) =>
                            updateDraft(key, { passportInfo: { ...draft.passportInfo, nationality: value } })
                          }
                        >
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.code} value={country.name}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </FloatingSelect>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 md:gap-3">
                        <DateSelectField
                          label="Passport expiry"
                          mode="future"
                          value={draft.passportInfo?.expiryDate || ''}
                          onChange={(value) =>
                            updateDraft(key, { passportInfo: { ...draft.passportInfo, expiryDate: value } })
                          }
                        />
                      </div>
                    </div>
                  </>
                )}

                {renderPreferences && (
                  <>
                    <hr />
                    {saved ? (
                      renderPreferences(saved)
                    ) : (
                      // Not stored yet: preferences ride in the draft and are created with the person.
                      <PreferenceList
                        label="Preferences"
                        value={draft.preferences}
                        onChange={(preferences) => updateDraft(key, { preferences })}
                        placeholder="Add a preference"
                      />
                    )}
                  </>
                )}

                <div className="flex items-center justify-end gap-3">
                  {error?.key === key && (
                    <p role="alert" className="mr-auto text-sm text-destructive">
                      {error.message}
                    </p>
                  )}
                  <Button variant="outline" size="sm" onClick={() => cancel(item)} disabled={busyKey === key}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => void save(item)} isBusy={busyKey === key}>
                    Save
                  </Button>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
